import React from "react"
import { navigate } from "gatsby"
import gql from "graphql-tag"
import { useMutation } from "react-apollo"
import PropTypes from "prop-types"
import { MdDeleteForever } from "react-icons/md"
import {
  useShowSuccessToast,
  Modal,
  ModalCard,
  StyledModal,
  StyledModalHeader,
  StyledModalBody,
} from "gatsby-interface"
import {
  SettingsCard,
  SettingsCardTitle,
  SettingsCardContent,
  SettingsCardDescription,
  SettingsCardEditButton,
} from "@modules/site/settings"
import { useTriggerErrorAlert } from "@modules/ui/components/ErrorAlert"
import { useTracker } from "@modules/analytics"
import { sites as text } from "@modules/locales/default.js"
import DeleteSiteForm from "./DeleteSiteForm"
import { SitePermissions } from "@modules/site/permissions"

const DELETE_SITE_MUTATION = gql`
  mutation deleteSite($id: UUID!) {
    deleteSite(id: $id) {
      success
      message
    }
  }
`

const siteNameCss = theme => ({
  color: theme.colors.red[60],
})

function DeleteSiteModal({ closeModal, siteName, siteId }) {
  const [deleteSiteMutation] = useMutation(DELETE_SITE_MUTATION)
  const [setError, errorAlert] = useTriggerErrorAlert()
  const showSuccessToast = useShowSuccessToast()
  const { trackButtonClicked } = useTracker()

  return (
    <StyledModal variant="WARNING">
      <StyledModalHeader onCloseButtonClick={closeModal}>
        {text.deleteSiteConf} <strong css={siteNameCss}>{siteName}?</strong>
      </StyledModalHeader>
      <StyledModalBody>
        {errorAlert}
        <DeleteSiteForm
          submitCallback={() => {
            setError(null)
            trackButtonClicked(text.yesDeleteSite, {
              siteId,
              uiSource: `Site settings`,
            })
            deleteSiteMutation({
              variables: {
                id: siteId,
              },
            })
              .then(({ data }) => {
                if (data.deleteSite.success) {
                  closeModal()
                  showSuccessToast(
                    <React.Fragment>
                      <strong>{siteName} </strong> {text.deletedSuccessfully}
                    </React.Fragment>
                  )
                  return navigate(`/dashboard/sites`)
                }
              })
              .catch(err => setError(err))
          }}
          cancelCallback={closeModal}
          siteName={siteName}
        />
      </StyledModalBody>
    </StyledModal>
  )
}

DeleteSiteModal.propTypes = {
  closeModal: PropTypes.func,
}

function DeleteSiteCard({ siteId, siteName, permissionToDelete }) {
  const [isDeleteModalOpened, setIsDeleteModalOpened] = React.useState(false)

  return (
    <React.Fragment>
      <SettingsCard>
        <SettingsCardTitle tone="DANGER" data-cy="delete-site-card-title">
          {text.deleteSite}
        </SettingsCardTitle>
        <SitePermissions id={siteId} resource="sites" action="delete">
          <SettingsCardEditButton
            onClick={() => setIsDeleteModalOpened(true)}
            data-cy="delete-site-button"
          >
            {text.deleteSite} <MdDeleteForever />
          </SettingsCardEditButton>
        </SitePermissions>
        <SettingsCardContent>
          <SettingsCardDescription>
            {permissionToDelete
              ? `${text.deleteSiteInfo}`
              : `${text.deleteSiteNoPermission}`}
          </SettingsCardDescription>
        </SettingsCardContent>
      </SettingsCard>
      {isDeleteModalOpened && (
        <Modal
          isOpen={isDeleteModalOpened}
          aria-label={`Do you want to remove that site?`}
          onDismiss={() => setIsDeleteModalOpened(false)}
          type="error"
        >
          <ModalCard>
            <DeleteSiteModal
              closeModal={() => setIsDeleteModalOpened(false)}
              siteName={siteName}
              siteId={siteId}
            />
          </ModalCard>
        </Modal>
      )}
    </React.Fragment>
  )
}

DeleteSiteCard.propTypes = {
  siteId: PropTypes.string.isRequired,
  permissionToDelete: PropTypes.bool,
}

export default DeleteSiteCard
